/**
 * Every string on the page lives here so copy can be edited without touching
 * layout. The wording comes from the client's "About TEEEM" brief
 * (`docs/ABOUT TEEEM.pdf`), which is the source of truth for this page — the
 * only additions are the short card summaries, which condense the brief's own
 * sentences to fit a grid.
 */

export const nav = [
  { label: 'About', href: '#about' },
  { label: 'Our Story', href: '#story' },
  { label: 'Our Vision', href: '#vision' },
  { label: 'Watch Us Grow', href: '#watch' },
  { label: 'Get Involved', href: '#get-involved' },
  { label: 'Contact', href: '#contact' },
];

export const hero = {
  eyebrow: 'Not a handout.',
  title: 'A hand up—and a walk together.',
  tagline: 'Teach. Evangelize. Equip. Empower.',
  body: 'We believe people need more than a handout. They need someone willing to walk alongside them.',
  cta: { label: 'Our Mission', href: '#mission' },
  image: 'hero-bg',
};

export const originQuote = {
  lead: 'TEEEM was born from a simple but challenging question: What would happen if we stopped merely telling people how to change their lives and started ',
  emphasis: 'walking alongside them',
  trail: ' while they did it?',
  becameVision: 'That question became a vision.',
  vision:
    'A vision for a Christ-centered community where people could belong, grow spiritually, develop practical skills, discover their purpose, become self-sufficient, and eventually reach back to help someone else.',
  becameTeeem: 'That vision became TEEEM.',
};

export const story = {
  intro:
    'TEEEM did not begin with a building, a large organization, or a bank account. It began with a burden for people.',
  burdens: [
    'People coming home from incarceration.',
    'People experiencing homelessness.',
    'People who have lost their direction.',
    'Families experiencing difficult circumstances.',
    'People searching for purpose.',
    'And people who simply need someone to believe there can still be another chapter to their story.',
  ],
  outro:
    'The original vision was for a place where people from different backgrounds could come together in a safe, Christ-centered community and rebuild.',
  place: [
    'Land',
    'Gardens',
    'Animals',
    'Work',
    'Housing',
    'Discipleship',
    'Education',
    'Responsibility',
    'Community',
    'Opportunity',
  ],
  notGiven: {
    lead: 'Not a place where everything would simply be given to someone, but a place where people could be ',
    emphasis: 'taught, equipped, and empowered',
    trail: ' to move forward.',
  },
  developed: 'Over time, that vision developed into what is now TEEEM.',
  images: [
    { name: 'story-1', alt: 'Two men talking together over an open notebook' },
    { name: 'story-2', alt: 'A man tending rows of vegetables in a garden' },
    { name: 'story-3', alt: 'A man learning woodworking in a workshop' },
    { name: 'story-4', alt: 'A circle of hands joined together' },
    { name: 'story-5', alt: 'A small group meeting together in a circle' },
    { name: 'story-6', alt: 'Two men caring for an animal on the property' },
  ],
  banner: ['Not a handout.', 'A hand up—and a walk together.'],
};

/**
 * The four actions the name stands for. `summary` is what shows in the grid;
 * `detail` is the brief's full treatment of each, revealed by the tabs below.
 */
export const pillars = [
  {
    id: 'teach',
    title: 'Teach',
    icon: '/assets/pillar-1.webp',
    summary:
      'We believe knowledge creates opportunity. We teach Biblical truth and practical skills for everyday life—equipping people to navigate today and build a better tomorrow.',
    detail: {
      lead: 'We believe knowledge creates opportunity.',
      body: 'TEEEM seeks to teach Biblical truth while also helping people develop the practical knowledge necessary to navigate everyday life. That can mean:',
      items: [
        'Understanding Scripture',
        'Learning financial responsibility',
        'Developing employment skills',
        'Learning how to communicate',
        'Understanding accountability',
        'Becoming a leader',
      ],
      close: 'Or simply learning something today that makes tomorrow different.',
    },
  },
  {
    id: 'evangelize',
    title: 'Evangelize',
    icon: '/assets/pillar-2.webp',
    summary:
      'We share the Gospel through words and actions. From the pulpit to practical service, we want people to encounter Christ through what we say and how we live.',
    detail: {
      lead: 'Jesus Christ is at the center of TEEEM.',
      body: 'We believe the Gospel should not only be spoken—it should be demonstrated through the way we treat people. Evangelism can happen from a pulpit. But it can also happen while:',
      items: [
        "Repairing someone's car",
        'Serving a meal',
        'Helping someone find employment',
        'Sitting beside someone during a difficult day',
        'Teaching a new skill',
        'Being present when someone believes nobody else cares',
      ],
      close: 'We want people to encounter Christ through both what we say and how we live.',
    },
  },
  {
    id: 'equip',
    title: 'Equip',
    icon: '/assets/pillar-3.webp',
    summary:
      "We provide tools, training, and support that prepare people for what's next. Our goal is not dependence—it's development that lasts.",
    detail: {
      lead: "Helping someone through today's problem matters. Preparing them for tomorrow matters too.",
      body: 'TEEEM seeks to equip people with tools they can continue using long after their immediate need has passed:',
      items: ['Spiritual tools', 'Personal tools', 'Vocational tools', 'Practical tools'],
      emphasis: ['Our goal is not dependence.', 'Our goal is development.'],
      close: 'We want people to leave an encounter with TEEEM better prepared for whatever comes next.',
    },
  },
  {
    id: 'empower',
    title: 'Empower',
    icon: '/assets/pillar-4.webp',
    summary:
      'We empower individuals to use their gifts, lead with confidence, make better decisions, and ultimately help someone else.',
    detail: {
      lead: 'Empowerment happens when someone begins using what they have learned.',
      body: 'A person discovers:',
      items: [
        'I can do this.',
        'I can work.',
        'I can contribute.',
        'I can lead.',
        'I can make better decisions.',
        'I can serve.',
        'I can help someone else.',
      ],
      emphasis: [
        'TEEEM ultimately seeks to help people move from receiving assistance to becoming someone capable of providing assistance to others.',
      ],
      close: 'That is when the circle becomes complete.',
    },
  },
];

export const mission = {
  lead: 'We recognize that people sometimes need immediate help. There will be times when the right thing to do is simply meet the need in front of us.',
  greater: 'But our greater purpose is to build relationships that create lasting change.',
  contrast: {
    notLabel: 'TEEEM does not want to stand in front of people saying:',
    not: '“Follow us.”',
    doLabel: 'We want to stand beside them saying:',
    do: "“Let's walk this road together.”",
  },
  body: [
    'We believe dignity grows through responsibility, opportunity, accountability, community, and purpose.',
    'And we believe everyone has something they can contribute.',
  ],
};

export const vision = {
  body: "TEEEM's long-term vision is to develop a Christ-centered, self-sustaining community where ministry and everyday life come together. We envision property that can eventually support:",
  goal: {
    not: 'The goal is not simply to build a facility.',
    is: 'The goal is to build people.',
    then: 'And then equip those people to build others.',
  },
  items: [
    { title: 'Transitional Housing', icon: '/assets/vision-1.webp' },
    { title: 'Biblical Discipleship', icon: '/assets/vision-2.webp' },
    { title: 'Agricultural Production', icon: '/assets/vision-3.webp' },
    { title: 'Vocational Training', icon: '/assets/vision-4.webp' },
    { title: 'Life-Skills Development', icon: '/assets/vision-5.webp' },
    { title: 'Leadership Development', icon: '/assets/vision-6.webp' },
    { title: 'Community Outreach', icon: '/assets/vision-7.webp' },
    { title: 'Mentorship', icon: '/assets/vision-8.webp' },
    { title: 'Secondhand Marketplace', icon: '/assets/vision-9.webp' },
    { title: 'Entrepreneurial Opportunities', icon: '/assets/vision-10.webp' },
    { title: 'Partnerships with Churches & Businesses', icon: '/assets/vision-11.webp' },
    { title: 'And More…', icon: '/assets/vision-12.webp' },
  ],
};

export const sustaining = {
  title: 'Building a Self-Sustaining Ministry',
  lead: 'We believe stewardship matters.',
  body: [
    "TEEEM's long-term goal is not to depend exclusively upon donations.",
    'Giving and charitable partnerships will always have an important place in the ministry, but we are also developing ways for TEEEM to generate resources through productive work.',
  ],
  marketplace: {
    title: 'The TEEEM Marketplace',
    body: 'The Marketplace is one part of that strategy. It begins with donated and secondhand goods and can grow alongside the ministry into additional products and opportunities.',
  },
  growth:
    "As TEEEM develops, agriculture, vocational programs, partnerships, products, and other mission-driven enterprises can help support the ministry's work.",
  desire: 'Our desire is simple:',
  steps: [
    { n: '01', text: 'Use what God provides wisely.' },
    { n: '02', text: 'Create something productive with it.' },
    { n: '03', text: 'Then use the increase to help someone else.' },
  ],
};

export const watch = {
  title: 'Watch the Story Happen',
  lead: 'Most organizations build privately and introduce themselves after everything is finished. We have chosen another approach.',
  invite: 'We are inviting you to see TEEEM while it is being built.',
  honest: [
    'Some parts of our vision already exist.',
    'Others are beginning.',
    'Others remain dreams we are actively working toward.',
  ],
  promise: 'We will not pretend to be somewhere we have not yet arrived.',
  instead: 'Instead, we invite you to watch.',
  items: [
    'the partnerships develop',
    'programs begin',
    'projects get completed',
    'needs get met',
    'resources grow',
    'people become involved',
    'the vision take shape',
  ],
  close: 'And watch what God does with people who are willing to make themselves available.',
};

export const foundation = {
  title: 'Our Biblical Foundation',
  intro: 'TEEEM is rooted in the Biblical model of preparing people for the work they have been called to do.',
  scripture:
    'And he gave some, apostles; and some, prophets; and some, evangelists; and some, pastors and teachers; For the perfecting of the saints, for the work of the ministry, for the edifying of the body of Christ.',
  reference: 'Ephesians 4:11–12',
  body: 'We believe ministry is not reserved for a handful of people standing behind pulpits.',
  items: [
    'People can be taught.',
    'People can grow.',
    'People can serve.',
    'People can discover their gifts.',
    'People can become leaders.',
  ],
  close:
    'And people who once needed someone to walk beside them can eventually become the person walking beside someone else.',
};

export const beginning = {
  title: 'This is only the beginning.',
  body: 'TEEEM is being built one relationship, one opportunity, one project, and one person at a time.',
  direction: "We don't know every door that will open along the way. But we know the direction we are headed.",
  words: ['Teach.', 'Evangelize.', 'Equip.', 'Empower.'],
  reach: 'And when someone becomes equipped and empowered, we encourage them to reach back for the next person.',
  invite: 'Come grow with us.',
  ctas: [
    { label: 'Watch Us Grow', href: '#watch', variant: 'outline' },
    { label: 'Get Involved', href: '#get-involved', variant: 'gold' },
    { label: 'Support TEEEM', action: 'donate', variant: 'dark' },
  ],
};

/**
 * Destinations for the calls to action. Fill in a `url` as each one becomes
 * available (a donation processor, a volunteer form); until then the subject
 * line is used to build an addressed mailto, so every button works today.
 */
export const actions = {
  donate: { url: '', subject: 'Giving to TEEEM' },
  volunteer: { url: '', subject: 'Volunteering with TEEEM' },
  partner: { url: '', subject: 'Partnering with TEEEM' },
  pray: { url: '', subject: 'Praying for TEEEM' },
};

export const getInvolved = {
  intro: 'You can be part of what God is doing through TEEEM.',
  cards: [
    {
      title: 'Volunteer',
      icon: 'helping-hand',
      body: 'Give your time and talents to help others grow and succeed.',
      cta: { label: 'Learn More', variant: 'outline', action: 'volunteer' },
    },
    {
      title: 'Give',
      icon: 'heart',
      body: 'Your financial support helps us provide resources, hope, and opportunity.',
      cta: { label: 'Donate Now', variant: 'gold', action: 'donate' },
    },
    {
      title: 'Partner',
      icon: 'handshake',
      body: 'Join with us as a church, business, or organization to expand the impact.',
      cta: { label: 'Partner With Us', variant: 'dark', action: 'partner' },
    },
    {
      title: 'Pray',
      icon: 'pray',
      body: 'Pray for the people we serve and the vision God has given us.',
      cta: { label: 'Pray With Us', variant: 'dark', action: 'pray' },
    },
  ],
};

export const newsletter = {
  title: 'Stay Connected',
  body: 'Get updates on stories, events, and ways to get involved.',
};

export const footer = {
  about:
    'A Christ-centered community teaching, equipping, and empowering people to transform their lives and impact the world.',
  quickLinks: nav,
  contact: {
    email: 'theteeem4126@gmail.com',
    phone: '(253) 240-5679',
    // Kept separate from the display format so the tel: link carries the
    // country code and dials correctly from abroad.
    phoneHref: '+12532405679',
    address: ['1456 S Kent Des Moines Rd', 'Des Moines, WA 98198'],
  },
  // The ministry has no social accounts, so the footer has no "Follow Us"
  // block. See the README for what to restore if that changes.
  verse: {
    text: "Carry each other's burdens, and in this way you will fulfill the law of Christ.",
    reference: '— Galatians 6:2',
  },
};
